import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { CircleNotch, ArrowClockwise, FloppyDisk, ArrowSquareOut } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AwinTab — paste an Awin "Create-a-Feed" URL per advertiser store, then run
 * the importer to replace fallback search URLs with real product deep-links
 * + live prices + stock status. Import runs as a background task that the
 * UI polls — proxy timeouts can't kill the job.
 */
export default function AwinTab() {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [urls, setUrls] = useState({});
  const pollIntervalRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/awin/status`, { withCredentials: true });
      setStores(data);
      setUrls(prev => {
        const next = { ...prev };
        for (const s of data) {
          if (next[s.store_id] === undefined) next[s.store_id] = s.awin_feed_url || '';
        }
        return next;
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll every 3s as long as any store reports awin_import_status === 'running'
  useEffect(() => {
    const anyRunning = stores.some(s => s.awin_import_status === 'running');
    if (anyRunning && !pollIntervalRef.current) {
      pollIntervalRef.current = setInterval(load, 3000);
    } else if (!anyRunning && pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [stores, load]);

  const saveUrl = async (storeId) => {
    setSavingKey(storeId);
    try {
      await axios.put(
        `${API}/admin/awin/feed-url/${storeId}`,
        { url: urls[storeId] || '' },
        { withCredentials: true }
      );
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setSavingKey(null);
    }
  };

  const runImport = async (storeId) => {
    try {
      await axios.post(`${API}/admin/awin/import/${storeId}`, {}, { withCredentials: true, timeout: 15000 });
      // Backend returns immediately; load() will reflect "running" and the poller kicks in
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  };

  const cancelImport = async (storeId) => {
    if (!window.confirm('Cancel this import? The background job may keep running for a few minutes but the UI will unlock immediately.')) return;
    try {
      await axios.post(`${API}/admin/awin/cancel/${storeId}`, {}, { withCredentials: true });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  };

  if (loading) return <div className="p-8 text-center"><CircleNotch size={32} className="animate-spin inline" /></div>;

  return (
    <div className="space-y-6" data-testid="awin-tab">
      <div className="border border-[#E4E4E7] bg-white p-6">
        <h3 className="font-heading font-bold text-lg mb-2">Awin Product-Feed Importer</h3>
        <p className="text-sm text-[#52525B] mb-4">
          Pull real product URLs + live prices + stock from Awin's product feed for each
          approved advertiser. Replaces the fallback search-URLs with proper deep-links so
          your commissions track and customers land on the right product page.
        </p>

        <div className="bg-[#F4F4F5] border border-[#E4E4E7] p-4 mb-6 text-xs space-y-2">
          <p className="font-bold uppercase tracking-wide text-[10px] text-[#52525B]">How to get the feed URL</p>
          <ol className="list-decimal pl-5 text-[#52525B] space-y-1">
            <li>Log in to <a href="https://ui.awin.com/" target="_blank" rel="noopener noreferrer" className="text-[#FF3B30] underline">ui.awin.com</a></li>
            <li>Open <strong>Toolbox → Create-a-Feed</strong></li>
            <li>Pick the advertiser, set <strong>Format = CSV</strong>, <strong>Compression = none or gzip</strong></li>
            <li>Tick the columns: <code className="bg-white px-1">aw_deep_link</code>, <code className="bg-white px-1">product_name</code>, <code className="bg-white px-1">merchant_image_url</code>, <code className="bg-white px-1">merchant_product_id</code>, <code className="bg-white px-1">display_price</code>, <code className="bg-white px-1">currency</code>, <code className="bg-white px-1">in_stock</code>, <code className="bg-white px-1">description</code></li>
            <li>Copy the generated URL (starts with <code className="bg-white px-1">https://productdata.awin.com/</code>) and paste it below for the matching store</li>
          </ol>
          <p className="text-[#52525B] pt-2 border-t border-[#E4E4E7] mt-2">
            <strong>Note:</strong> Awin can take 30–120 seconds to generate large feeds. The import runs in the background — this page auto-refreshes the status every 3 seconds. <strong>The importer will also auto-create new shoes</strong> from feed rows we don't have yet (up to 60 per run, only those with an image and a price).
          </p>
        </div>

        {stores.length === 0 && (
          <p className="text-sm text-[#52525B] italic">No Awin stores configured yet. Set affiliate_network=awin on a store first (Affiliate tab).</p>
        )}

        <div className="space-y-4">
          {stores.map(s => {
            const isSaving = savingKey === s.store_id;
            const isRunning = s.awin_import_status === 'running';
            const lastStatus = s.awin_import_status;
            const hasFeed = !!s.awin_feed_url;
            // Warn if the saved URL has the wrong fid or multiple advertiser IDs
            const expectedFid = s.affiliate_advertiser_id;
            let urlWarning = null;
            if (hasFeed && expectedFid) {
              const m = s.awin_feed_url.match(/\/fid\/([^/]+)/);
              if (m) {
                const fids = m[1].split(',');
                if (fids.length > 1) {
                  urlWarning = `URL covers ${fids.length} advertisers. Should be only ${expectedFid}. Imports may time out.`;
                } else if (fids[0] !== String(expectedFid)) {
                  urlWarning = `URL points to advertiser ${fids[0]} but this store expects ${expectedFid}.`;
                }
              }
            }
            return (
              <div key={s.store_id} className="border-2 border-[#E4E4E7] p-4" data-testid={`awin-store-${s.store_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                  <div>
                    <p className="font-heading font-black text-base uppercase tracking-tight">{s.name}</p>
                    <p className="text-[10px] font-bold uppercase tracking-wide text-[#52525B] mt-0.5">
                      {s.region} · Awin · advertiser #{s.affiliate_advertiser_id || '—'}
                    </p>
                  </div>
                  {s.awin_last_import_at && !isRunning && (
                    <div className="text-right text-[10px] text-[#52525B]">
                      Last import: {new Date(s.awin_last_import_at).toLocaleString()}<br />
                      {s.awin_last_import_matched ?? 0} / {s.awin_last_import_rows ?? 0} matched · {s.awin_last_import_updated ?? 0} updated
                    </div>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-2 mb-3">
                  <input
                    type="url"
                    placeholder="https://productdata.awin.com/datafeed/download/apikey/.../..."
                    value={urls[s.store_id] ?? ''}
                    onChange={e => setUrls({ ...urls, [s.store_id]: e.target.value })}
                    disabled={isRunning}
                    className="flex-1 border-2 border-[#E4E4E7] px-3 py-2 text-xs font-mono focus:border-[#0A0A0A] outline-none disabled:bg-[#F4F4F5]"
                    data-testid={`awin-url-${s.store_id}`}
                  />
                  <button
                    onClick={() => saveUrl(s.store_id)}
                    disabled={isSaving || isRunning}
                    className="bg-[#0A0A0A] text-white font-bold uppercase tracking-wide px-4 py-2 text-xs hover:bg-[#52525B] transition-colors disabled:opacity-50 flex items-center gap-2"
                    data-testid={`awin-save-${s.store_id}`}
                  >
                    {isSaving ? <CircleNotch size={12} className="animate-spin" /> : <FloppyDisk size={12} weight="bold" />}
                    Save URL
                  </button>
                </div>

                {urlWarning && (
                  <div className="mb-3 bg-[#FFFBEB] border border-[#F59E0B] p-2 text-[11px] text-[#92400E]" data-testid={`awin-url-warning-${s.store_id}`}>
                    ⚠ <strong>URL mismatch:</strong> {urlWarning} Regenerate in Awin Toolbox with only this advertiser selected.
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-3">
                  {!isRunning ? (
                    <button
                      onClick={() => runImport(s.store_id)}
                      disabled={!hasFeed}
                      className="bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-4 py-2 text-xs hover:bg-[#D62828] transition-colors disabled:opacity-50 flex items-center gap-2"
                      data-testid={`awin-import-${s.store_id}`}
                    >
                      <ArrowClockwise size={12} weight="bold" />
                      {hasFeed ? 'Run import now' : 'Save URL first'}
                    </button>
                  ) : (
                    <>
                      <button
                        disabled
                        className="bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-4 py-2 text-xs opacity-60 flex items-center gap-2"
                        data-testid={`awin-import-${s.store_id}`}
                      >
                        <CircleNotch size={12} className="animate-spin" />
                        Importing in background…
                      </button>
                      <button
                        onClick={() => cancelImport(s.store_id)}
                        className="bg-white text-[#FF3B30] border-2 border-[#FF3B30] font-bold uppercase tracking-wide px-3 py-1.5 text-xs hover:bg-[#FF3B30] hover:text-white transition-colors"
                        data-testid={`awin-cancel-${s.store_id}`}
                      >
                        Cancel
                      </button>
                    </>
                  )}
                  {s.awin_feed_url && (
                    <a href={s.awin_feed_url} target="_blank" rel="noopener noreferrer"
                      className="text-[11px] text-[#52525B] hover:text-[#FF3B30] underline flex items-center gap-1">
                      Open feed URL <ArrowSquareOut size={11} />
                    </a>
                  )}
                </div>

                {isRunning && (
                  <div className="mt-3 bg-[#FEF3C7] border border-[#F59E0B] p-3 text-xs" data-testid={`awin-result-running-${s.store_id}`}>
                    <p className="font-bold text-[#92400E] mb-1 flex items-center gap-2">
                      <CircleNotch size={12} className="animate-spin" /> Import running…
                    </p>
                    <p className="text-[#92400E]">
                      Started {s.awin_import_started_at ? new Date(s.awin_import_started_at).toLocaleTimeString() : ''}.
                      Awin is generating the CSV — this can take 30–120s. Status refreshes every 3s.
                    </p>
                  </div>
                )}
                {!isRunning && lastStatus === 'ok' && (
                  <div className="mt-3 bg-[#F0FDF4] border border-[#10B981] p-3 text-xs" data-testid={`awin-result-ok-${s.store_id}`}>
                    <p className="font-bold text-[#10B981] mb-1">
                      Import OK · {s.awin_last_import_took_seconds ?? '?'}s
                    </p>
                    <div className="text-[#52525B] grid grid-cols-2 sm:grid-cols-3 gap-y-1 gap-x-4">
                      <div>Feed rows: <strong>{s.awin_last_import_rows ?? 0}</strong></div>
                      <div>Matched: <strong>{s.awin_last_import_matched ?? 0}</strong></div>
                      <div>Updated: <strong>{s.awin_last_import_updated ?? 0}</strong></div>
                      <div>New shoes created: <strong className="text-[#10B981]">{s.awin_last_import_created ?? 0}</strong></div>
                      <div>Placeholders fixed: <strong className="text-[#10B981]">{s.awin_last_import_images_refreshed ?? 0}</strong></div>
                      <div>Catalog total: <strong>{s.awin_last_import_catalog_total ?? '?'}</strong></div>
                    </div>
                    {s.awin_last_import_unmatched_sample?.length > 0 && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-[#52525B]">Unmatched sample (5 of many)</summary>
                        <ul className="mt-1 pl-4 list-disc text-[#52525B]">
                          {s.awin_last_import_unmatched_sample.map((n, i) => <li key={i}>{n}</li>)}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
                {!isRunning && lastStatus === 'error' && (
                  <div className="mt-3 bg-[#FFF5F4] border border-[#FF3B30] p-3 text-xs text-[#7C2D12]" data-testid={`awin-result-err-${s.store_id}`}>
                    <p className="font-bold text-[#FF3B30] mb-1">Import failed</p>
                    <p className="break-words">{s.awin_import_error || 'Unknown error'}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
